import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Loader2, ExternalLink, ShieldCheck } from 'lucide-react'
import api from '../../services/api'
import { useTranslation } from 'react-i18next'

/**
 * Launchpad — "one login, many products".
 *
 * Shows only the products this person may open: ProTrack (their ordinary account IS their
 * ProTrack access) plus any product they have been explicitly granted. Being in one product
 * never grants a role in another, so nothing appears here by accident.
 *
 * A Super Admin sees every active product — that is precisely what Super Admin means.
 */

const ACCENTS = {
  indigo: 'from-indigo-500 to-indigo-600',
  blue: 'from-blue-500 to-blue-600',
  emerald: 'from-emerald-500 to-emerald-600',
  amber: 'from-amber-500 to-amber-600',
  rose: 'from-rose-500 to-rose-600',
  purple: 'from-purple-500 to-purple-600',
  slate: 'from-slate-500 to-slate-600',
}

export default function Launchpad() {
  const { t } = useTranslation('platform')
  const navigate = useNavigate()
  const [data, setData] = useState(null)
  const [error, setError] = useState(null)

  useEffect(() => {
    let alive = true
    api.get('/platform/launchpad')
      .then(r => { if (alive) setData(r.data) })
      .catch(e => { if (alive) setError(e.response?.data?.detail || 'Could not load your products.') })
    return () => { alive = false }
  }, [])

  const open = (tile) => {
    // ProTrack's own tile goes straight to the dashboard. Every other product opens INSIDE
    // ProTrack, where its usage figures and any screens it supplies are drawn — with a link out
    // to the product itself. A tile with no address is therefore still worth opening: the page
    // explains what is missing rather than doing nothing when clicked.
    if (tile.key === 'protrack') { navigate(tile.url || '/dashboard'); return }
    navigate(`/products/${tile.key}`)
  }

  if (error) {
    return <div className="p-6"><p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{error}</p></div>
  }
  if (!data) {
    return <div className="p-6 flex items-center gap-2 text-sm text-gray-400"><Loader2 className="w-4 h-4 animate-spin" /> Loading your products…</div>
  }

  return (
    <div className="p-6 max-w-5xl mx-auto">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">{t('your_products')}</h1>
        <p className="text-sm text-gray-500 mt-1">
          One login across 3 Pillars. Each product keeps its own roles and its own data — your role
          here doesn't change what you can do there.
        </p>
        {data.is_super_admin && (
          <span className="inline-flex items-center gap-1.5 mt-3 text-xs font-semibold text-amber-800 bg-amber-50 border border-amber-200 rounded-full px-2.5 py-1">
            <ShieldCheck className="w-3.5 h-3.5" /> Super Admin — you can see every product
          </span>
        )}
      </div>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {data.products.map((t, i) => (
          <button
            key={t.key || i}
            onClick={() => open(t)}
            className="text-left bg-white rounded-2xl border border-gray-200 shadow-sm hover:shadow-md hover:border-indigo-200 transition overflow-hidden"
          >
            <div className={`h-1.5 bg-linear-to-r ${ACCENTS[t.colour] || ACCENTS.slate}`} />
            <div className="p-4">
              <div className="flex items-start justify-between gap-2">
                <span className="text-2xl leading-none">{t.icon || '📦'}</span>
                {t.url && !t.url.startsWith('/') && <ExternalLink className="w-3.5 h-3.5 text-gray-300 mt-1" />}
              </div>
              <p className="mt-2 font-semibold text-gray-900">{t.name}</p>
              {t.description && <p className="text-xs text-gray-500 mt-0.5 line-clamp-2">{t.description}</p>}
              <p className="mt-3 text-[11px] text-gray-400">
                Your role here: <span className="font-medium text-gray-600">
                  {t.my_role || (t.access_via === 'protrack_super_admin'
                    ? 'none — you see it as ProTrack Super Admin'
                    : '—')}
                </span>
              </p>
              {!t.url && <p className="mt-1 text-[10px] text-amber-600">{t('no_address_yet')}</p>}
            </div>
          </button>
        ))}
      </div>

      {data.products.length <= 1 && (
        <p className="mt-6 text-xs text-gray-400">
          Only ProTrack so far. When you're given access to another product it will appear here —
          access is always granted deliberately, never automatically.
        </p>
      )}
    </div>
  )
}
